import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Input } from 'components/FormFields';

// Form card container
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr 1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Three column grid for addresses
const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AddressCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.background?.overlay ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  padding: 20px;
`;

const AddressTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddressFormGroup = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Icon
const MapPinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const AddressSection = ({ title, addressKey, values, setFieldValue }) => {
  const address = values[addressKey] || {};

  const handleAddressChange = (field, value) => {
    setFieldValue(addressKey, {
      ...address,
      [field]: value,
      type: addressKey,
    });
  };

  return (
    <AddressCard>
      <AddressTitle>{title}</AddressTitle>
      <AddressFormGroup>
        <Input
          label="Recipient"
          value={address.name || ''}
          onChange={(e) => handleAddressChange('name', e.target.value)}
          placeholder="Enter recipient name"
        />
      </AddressFormGroup>
      <AddressFormGroup>
        <Input
          label="Address Line 1"
          value={address.address_line_1 || ''}
          onChange={(e) =>
            handleAddressChange('address_line_1', e.target.value)
          }
          placeholder="Street address"
        />
      </AddressFormGroup>
      <AddressFormGroup>
        <Input
          label="Address Line 2"
          value={address.address_line_2 || ''}
          onChange={(e) =>
            handleAddressChange('address_line_2', e.target.value)
          }
          placeholder="Apt, suite, unit, etc. (optional)"
        />
      </AddressFormGroup>
      <AddressFormGroup>
        <Input
          label="City"
          value={address.city || ''}
          onChange={(e) => handleAddressChange('city', e.target.value)}
          placeholder="City"
        />
      </AddressFormGroup>
      <FormRow columns="1fr 1fr">
        <Input
          label="State"
          value={address.state || ''}
          onChange={(e) => handleAddressChange('state', e.target.value)}
          placeholder="State"
        />
        <Input
          label="Zipcode"
          value={address.zipcode || ''}
          onChange={(e) => handleAddressChange('zipcode', e.target.value)}
          placeholder="Zipcode"
        />
      </FormRow>
    </AddressCard>
  );
};

AddressSection.propTypes = {
  title: PropTypes.string.isRequired,
  addressKey: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

const AddressesTab = ({ values, setFieldValue }) => {
  return (
    <FormCard>
      <FormSection noMargin>
        <SectionTitle>
          <MapPinIcon />
          Addresses
        </SectionTitle>

        <AddressGrid>
          <AddressSection
            title="Physical Address"
            addressKey="track"
            values={values}
            setFieldValue={setFieldValue}
          />
          <AddressSection
            title="Mailing Address"
            addressKey="mailing"
            values={values}
            setFieldValue={setFieldValue}
          />
          <AddressSection
            title="Shipping Address"
            addressKey="shipping"
            values={values}
            setFieldValue={setFieldValue}
          />
        </AddressGrid>
      </FormSection>
    </FormCard>
  );
};

AddressesTab.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default AddressesTab;
